import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiX, FiLoader } from "react-icons/fi";
import { apiRequest } from "../api";

// Pieces shared by the audio tools that upload recordings and run background jobs on the server:
// AI Voice Cloning and AI Music Generation (music only).

const CLIENT_ID_KEY = "voice_clone_client_id";
const ACCEPTED_DIRECTLY = [".wav", ".mp3", ".flac", ".ogg"];
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
export const MAX_SONG_BYTES = 40 * 1024 * 1024;
export const LIMITS = { original: 120, reference: 3, song: 360, studio: 45 };   // seconds: longest original, shortest reference, longest song


export function getClientId() {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id = `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch {
    return `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

export function formatSeconds(seconds) {
  if (seconds === null || seconds === undefined) return "";
  const m = Math.floor(seconds / 60);
  return `${m}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;
}

export function formatSize(bytes) {
  return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// Phone voice notes (m4a, aac, webm, ...) are not read by the server: decode them in the browser and
// send a small mono WAV instead.
export async function toWav(file) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioCtx();
  let decoded;
  try {
    decoded = await ctx.decodeAudioData(await file.arrayBuffer());
  } finally {
    ctx.close();
  }
  const rate = 22050;
  const offline = new OfflineAudioContext(1, Math.max(1, Math.ceil(decoded.duration * rate)), rate);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const samples = (await offline.startRendering()).getChannelData(0);

  const view = new DataView(new ArrayBuffer(44 + samples.length * 2));
  const write = (offset, text) => [...text].forEach((c, i) => view.setUint8(offset + i, c.charCodeAt(0)));
  write(0, "RIFF"); view.setUint32(4, 36 + samples.length * 2, true); write(8, "WAVEfmt ");
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  write(36, "data"); view.setUint32(40, samples.length * 2, true);
  samples.forEach((s, i) => view.setInt16(44 + i * 2, Math.max(-1, Math.min(1, s)) * 0x7fff, true));
  return new File([view.buffer], file.name.replace(/\.[^.]+$/, "") + ".wav", { type: "audio/wav" });
}

export function UploadCard({ step, title, help, icon: Icon, kind, picked, onPick, onClear, problem, maxBytes = MAX_UPLOAD_BYTES, convertOthers = true }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [url, setUrl] = useState("");
  const [seconds, setSeconds] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!picked) {
      setUrl("");
      setSeconds(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(picked);
    setUrl(objectUrl);
    const probe = new Audio(objectUrl);
    probe.onloadedmetadata = () => setSeconds(Number.isFinite(probe.duration) ? probe.duration : null);
    return () => URL.revokeObjectURL(objectUrl);
  }, [picked]);

  const accept = async (file) => {
    if (!file) return;
    setError("");
    const lower = file.name.toLowerCase();
    let ready = file;
    if (!ACCEPTED_DIRECTLY.some((ext) => lower.endsWith(ext))) {
      if (!convertOthers) return setError("Please use a WAV, MP3, FLAC or OGG file for the best music quality.");
      setBusy(true);
      try {
        ready = await toWav(file);
      } catch {
        setBusy(false);
        return setError("This file could not be read as audio. Please use a WAV, MP3, FLAC, OGG, M4A or WEBM recording.");
      }
      setBusy(false);
    }
    if (ready.size > maxBytes) return setError(`This file is larger than ${maxBytes / 1024 / 1024} MB.`);
    onPick(ready);
  };

  const lengthWarning = seconds === null ? "" :
    kind === "studio" && seconds > LIMITS.studio ? `This clip is ${formatSeconds(seconds)} long. Studio clarity works on clips up to ${LIMITS.studio} seconds - use "Remove noise" for longer recordings.` :
    kind === "song" && seconds > LIMITS.song ? `This song is ${formatSeconds(seconds)} long. The limit is ${LIMITS.song / 60} minutes.` :
    kind === "original" && seconds > LIMITS.original ? `This recording is ${formatSeconds(seconds)} long. The limit is ${LIMITS.original} seconds.` :
    kind === "reference" && seconds < LIMITS.reference ? `This sample is only ${seconds.toFixed(1)} seconds. Use at least ${LIMITS.reference} seconds - 10 to 20 seconds of clear speech works best.` : "";

  return (
    <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
      <div className="flex items-center gap-3 mb-1">
        <span className="w-7 h-7 rounded-full bg-brand-gradient flex items-center justify-center text-sm font-bold">{step}</span>
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      <p className="text-sm text-gray-400 mb-4">{help}</p>

      {!picked ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); accept(e.dataTransfer.files?.[0]); }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
          className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors ${
            dragging ? "border-accent-light bg-accent/10" : "border-navy-border hover:border-accent-light/70"
          }`}
        >
          {busy ? <FiLoader className="mx-auto animate-spin text-accent-light" size={30} /> : <Icon className="mx-auto text-accent-light" size={30} />}
          <p className="mt-3 text-sm text-gray-200">{busy ? "Preparing the recording..." : "Click to choose a recording, or drop it here"}</p>
          <p className="text-xs text-gray-500 mt-1">{convertOthers ? "WAV, MP3, FLAC, OGG, M4A" : "WAV, MP3, FLAC, OGG"} - up to {maxBytes / 1024 / 1024} MB</p>
        </div>
      ) : (
        <div className="rounded-xl bg-navy border border-navy-border p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{picked.name}</p>
              <p className="text-xs text-gray-500">{formatSize(picked.size)}{seconds !== null && ` · ${formatSeconds(seconds)}`}</p>
            </div>
            <button type="button" onClick={onClear} aria-label="Remove recording" className="text-gray-400 hover:text-red-400 shrink-0"><FiX size={18} /></button>
          </div>
          {url && <audio controls src={url} className="w-full h-10" />}
        </div>
      )}

      <input ref={inputRef} type="file" accept="audio/*,.wav,.mp3,.flac,.ogg,.m4a,.aac,.webm" className="hidden"
        onChange={(e) => { accept(e.target.files?.[0]); e.target.value = ""; }} />
      {(error || lengthWarning || problem) && <p className="text-sm text-amber-300 mt-3">{error || problem || lengthWarning}</p>}
    </div>
  );
}


// The visitor's jobs of one kind ("clone" | "instrumental"): loads them, polls while one is running and
// offers delete. No login - the browser's own client id finds them again.
export function useJobHistory(kind) {
  const [clientId] = useState(getClientId);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    const { ok, data } = await apiRequest(`/core/voice-clone/?client_id=${encodeURIComponent(clientId)}`);
    if (ok) setHistory(data.filter((job) => job.kind === kind));
    setLoading(false);
  }, [clientId, kind]);

  useEffect(() => { load(); }, [load]);

  const running = history.find((job) => job.status === "queued" || job.status === "processing");
  useEffect(() => {
    if (!running) return undefined;
    const poll = setInterval(load, 3000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [running, load]);

  const add = (job) => {
    setHistory((prev) => [job, ...prev.filter((j) => j.id !== job.id)]);
    setNow(Date.now());
  };

  const remove = async (job) => {
    if (!window.confirm("Delete this result?")) return;
    await apiRequest(`/core/voice-clone/${job.id}/?client_id=${encodeURIComponent(clientId)}`, { method: "DELETE" });
    setHistory((prev) => prev.filter((j) => j.id !== job.id));
  };

  const elapsed = running ? Math.max(0, Math.round((now - new Date(running.created_at).getTime()) / 1000)) : 0;
  return { clientId, history, loading, running, latest: history[0], elapsed, add, remove };
}
